import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { ButtonGroup, Spacing, Text } from '@vkontakte/vkui';
import config from 'Cloud/config';
import classNames from 'clsx';
import { ReactComponent as FolderIcon } from 'img/folderIcon.svg';
import { extInfo } from 'lib/extInfo';
import { getFaviconByExtension } from 'lib/getFaviconByExtension';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { overquotaNewUnsub } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getHomeFolderItems } from 'reactApp/modules/home/home.selectors';
import type { HomeFile, HomeItem } from 'reactApp/modules/home/home.types';
import { closePopupHelper, openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { OverquotaSubscriptionWidget } from 'reactApp/ui/OverquotaFilesModal/OverquotaSubscriptionWidget';
import { sendDwh } from 'reactApp/utils/ga';
import { truncateTextMiddle } from 'reactApp/utils/textHelpers';

import styles from './OverquotaFilesModal.css';

interface Props {
    subscription: BillingDataSubscriptionInfo;
    isCardUnlink?: boolean;
    onClose?: () => void;
    onCancel?: () => void;
    onSuccess?: () => void;
    onAction?: () => void;
}

const sendRadar = (action: string, count_files: number) => {
    sendDwh({
        label: `my-tariff-cancel-ovr-fls-${action}`,
        eventCategory: 'payment',
        dwh_enabled: true,
        dwhData: { count_files },
    });
};

const sendCardUnlinkRadar = (action: string, count_files: number) => {
    const cardAction = action === 'save' || action === 'cancel' ? 'click' : action;
    const buttonName = action === 'save' ? 'keep_card' : action === 'cancel' ? 'risk_files' : undefined;
    const platform = EnvironmentSelectors.isMobile() ? 'touch' : 'web';

    sendDwh({
        action: cardAction,
        eventCategory: 'modal',
        dwh_enabled: true,
        dwhData: {
            name: 'mnz_card_unlink_outflow_overquota_files',
            platform,
            count_files,
            ...(buttonName ? { button_name: buttonName } : {}),
        },
    });
};

const messages = {
    description: 'Эти файлы могут не поместиться',
    buttonPrimary: 'Сохранить подписку',
    buttonSecondary: 'Рискну важными файлами',
};

const cardUnlinkMessages = {
    description: messages.description,
    buttonPrimary: 'Оставить карту',
    buttonSecondary: 'Рискну файлами',
};

const buildUrls = config.get('BUILD_URLS');
const imgPath = buildUrls.img;

export const OverquotaFilesModal = (props: Props) => {
    const { isCardUnlink, onCancel, onClose: onCloseProp, subscription } = props;
    const homeItems = useSelector(getHomeFolderItems);
    const currentMessages = isCardUnlink ? cardUnlinkMessages : messages;
    const isMobile = EnvironmentSelectors.isMobile();
    const ref = useRef<HTMLDivElement>(null);
    const maxFileNameLength = isMobile ? 20 : 28;

    const availableSpace = useSelector(UserQuotaSelectors.getTotalOrBase);
    const spaceLeft = availableSpace - subscription.space.original;

    const itemsToShow = homeItems
        .filter((item): item is HomeItem => !!item)
        .sort((a, b) => b.size - a.size)
        .slice(0, overquotaNewUnsub.foldersCount)
        .map((item) => {
            if (item.isFolder) {
                return {
                    type: 'folder',
                    name: item.name,
                    size: item.size,
                    icon: <FolderIcon className={styles.icon} />,
                };
            }
            const extension = extInfo.get((item as HomeFile).ext);
            const icon = getFaviconByExtension(extension, imgPath).svg;
            return {
                type: 'file',
                name: item.name,
                size: item.size,
                icon: <img src={icon ? icon : undefined} className={styles.icon} />,
            };
        });

    const count = itemsToShow.length;

    useEffect(() => {
        if (isCardUnlink) {
            sendCardUnlinkRadar('show', count);
        } else {
            sendRadar('show', count);
        }
    }, [count, isCardUnlink]);

    const onClick = useCallback(() => {
        onCloseProp?.();
        if (isCardUnlink) {
            sendCardUnlinkRadar('save', count);
        } else {
            sendRadar('save', count);
        }
    }, [count, isCardUnlink, onCloseProp]);

    const onSecondaryClick = useCallback(() => {
        onCancel?.();
        if (isCardUnlink) {
            sendCardUnlinkRadar('cancel', count);
        } else {
            sendRadar('cancel', count);
        }
    }, [count, isCardUnlink, onCancel]);

    const onClose = useCallback(() => {
        onCloseProp?.();
        if (isCardUnlink) {
            sendCardUnlinkRadar('close', count);
        } else {
            sendRadar('close', count);
        }
    }, [count, isCardUnlink, onCloseProp]);

    const getTitle = useCallback(() => {
        return `Без подписки останется только ${bytesToNDigits(spaceLeft, 3).value}`;
    }, [spaceLeft]);

    const buttons = useMemo(() => {
        return (
            <ButtonGroup mode={isMobile ? 'vertical' : 'horizontal'} gap={'s'} stretched={isMobile}>
                <Button
                    qaId={'modal-primary-btn'}
                    theme={'vk'}
                    primary
                    onClick={onClick}
                    sizeMode={ButtonSizeMode.small}
                    className={classNames(styles.button, { [styles.button_mobile]: isMobile })}
                >
                    {currentMessages.buttonPrimary}
                </Button>
                <Button
                    qaId={'modal-secondary-btn'}
                    theme={'vk'}
                    secondary
                    onClick={onSecondaryClick}
                    sizeMode={ButtonSizeMode.small}
                    className={classNames(styles.button, { [styles.button_mobile]: isMobile })}
                >
                    {currentMessages.buttonSecondary}
                </Button>
            </ButtonGroup>
        );
    }, [currentMessages.buttonPrimary, currentMessages.buttonSecondary, isMobile, onClick, onSecondaryClick]);

    const content = useMemo(() => {
        return (
            <>
                <Spacing size={32} />
                <Text className={isMobile ? styles.title_mobile : styles.title}>{getTitle()}</Text>
                <Spacing size={24} />
                <OverquotaSubscriptionWidget isMobile={isMobile} availableSpace={availableSpace} spaceLeft={spaceLeft} />
                {count > 0 && (
                    <>
                        <Spacing size={24} />
                        <Text className={styles.subheader}>{currentMessages.description}</Text>
                        <Spacing size={16} />
                        <div className={styles.items}>
                            {itemsToShow.map((item, index) => (
                                <div key={`file-${index}`} className={styles.itemRow}>
                                    <div className={styles.itemName}>
                                        {item.icon}
                                        <span className={styles.textEllipsis}>{truncateTextMiddle(item.name, maxFileNameLength)}</span>
                                    </div>
                                    <span className={styles.itemSize}>{bytesToNDigits(item.size, 3).value}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <Spacing size={36} />
                {!isMobile && (
                    <>
                        {buttons}
                        <Spacing size={32} />
                    </>
                )}
            </>
        );
    }, [availableSpace, buttons, count, currentMessages.description, getTitle, isMobile, itemsToShow, maxFileNameLength, spaceLeft]);

    if (isMobile) {
        return (
            <MobileDialog
                id={'outflow-overquota-warning-modal'}
                open
                onClose={onClose}
                closable
                mod={'confirm'}
                topmost
                scrollRef={ref}
                isRebrandingQuotaLanding
                dimmer
                closeOnDimmerClick
                footer={buttons}
                className={styles.card}
            >
                <div ref={ref} className={styles.card}>
                    {content}
                </div>
            </MobileDialog>
        );
    }

    return (
        <Dialog
            open
            id={'outflow-overquota-warning-modal'}
            onCancel={onClose}
            closable
            closeOnEscape={true}
            closeOnDimmerClick={false}
            className={styles.card}
            size={'tiny'}
            dimmer={false}
        >
            <div ref={ref} className={styles.card}>
                {content}
            </div>
        </Dialog>
    );
};

export const openOverquotaFilesModal = (data?: Props) =>
    openPopupHelper({ popupName: popupNames.OVERQUOTA_FILES_MODAL, data, onClose: data?.onClose });

export const closeOverquotaFilesModal = () => closePopupHelper(popupNames.OVERQUOTA_FILES_MODAL);
